import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider as StyledThemeProvider } from "styled-components";
import { ToastContainer } from 'react-toastify';
import Home from './pages/Home';
import Cases from './components/Cases';
import ProfileSection from './components/ProfileSection';
import CaseView from './components/CaseView';
import CaseResult from './components/CaseResult';
import SkinDetails from './components/SkinDetails';
import Packages from './pages/Packages';
import TradeUrlPage from './components/TradeUrlPage';
import TokenFarming from './components/TokenFarming';
import MusicPage from './components/MusicPage';
import Referrals from './components/Referrals';
import MySkins from './components/MySkins';
import SupportPage from './components/SupportPage';
import TicketChat from './components/TicketChat';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import { setupAudioUnlock } from './utils/sound';
import { SoundProvider } from './contexts/SoundContext';
import { useAppLanguage } from './hooks/useAppLanguage';
import { CollectionProvider } from './contexts/CollectionContext';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import WebApp from '@twa-dev/sdk';
import WithdrawPage from './components/WithdrawPage';
import useUserStore from './stores/userStore';
import usePackageStore from './stores/packageStore';
import useCasesStore from './stores/casesStore';
import useReferralStore from './stores/referralStore';
import useInventoryStore from './stores/inventoryStore';
import { useTranslation } from 'react-i18next';
import ScrollToTop from './components/ScrollToTop';
import LoginView from './components/LoginView';
import OnboardingView from './components/OnboardingView';
import React from 'react';
import { AgreementContext } from './contexts/AgreementContext';
import { QuizProvider } from './contexts/QuizContext';
import { useMaintenanceCheck } from './hooks/useMaintenanceCheck';
import ReactPixel from 'react-facebook-pixel';
import BannedPage from './pages/BannedPage';
import CaseContentsView from './components/CaseContentsView';
import useChallengeStore from './stores/challengeStore';
import LanguagePage from './pages/LanguagePage';
import ClaimFragsPage from './pages/ClaimFragsPage';

const GlobalStyles = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text.primary};
    overflow: hidden;
  }

  * {
    box-sizing: border-box;
  }

  /* Стили для скроллбара */
  ::-webkit-scrollbar {
    width: 0px;
  }

  /* Стили для toast-уведомлений */
  .Toastify__toast {
    background: ${({ theme }) => theme.card.background};
    color: ${({ theme }) => theme.text.primary};
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .Toastify__toast-body {
    font-size: 14px;
    font-weight: 500;
  }

  .Toastify__progress-bar {
    background: ${({ theme }) => theme.primary};
  }
`;

const AppContent = ({ setAgreed }) => {
  const location = useLocation();
  const { currentTheme } = useThemeContext();
  const auth = useUserStore(state => state.auth);
  const fetchUser = useUserStore(state => state.fetchUser);
  const fetchPackages = usePackageStore(state => state.fetchPackages);
  const fetchCases = useCasesStore(state => state.fetchCases);
  const fetchReferrals = useReferralStore(state => state.fetchReferrals);
  const fetchItems = useInventoryStore(state => state.fetchItems);
  const fetchChallenges = useChallengeStore(state => state.fetchChallenges);
  useAppLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agreed } = React.useContext(AgreementContext);
  const user = useUserStore(state => state.user);

  const [agreementsChecked, setAgreementsChecked] = React.useState(false);
  const [pendingStartParam, setPendingStartParam] = React.useState(null);
  const [userLoaded, setUserLoaded] = React.useState(false);

  // Проверяем время регистрации и автоматически управляем флагом квиза
  React.useEffect(() => {
    if (user && user.createdAt) {
      const timeDiff = Date.now() - new Date(user.createdAt).getTime();
      const isExpired = timeDiff > 0 && timeDiff > 2 * 60 * 1000; // Больше 2 минут
      const hasCompletedQuiz = localStorage.getItem('quizCompleted');

      console.log('App.js: Проверка времени регистрации:', {
        createdAt: user.createdAt,
        timeDiff: timeDiff,
        isExpired: isExpired,
        hasCompletedQuiz: hasCompletedQuiz
      });

      if (isExpired) {
        console.log('App.js: Время регистрации истекло, помечаем квиз как пройденный');
        localStorage.setItem('quizCompleted', 'true');
      } else if (!hasCompletedQuiz) {
        // Удаляем флаг квиза только если его еще нет (для новых пользователей)
        console.log('App.js: Время регистрации не истекло и квиз не пройден, удаляем флаг квиза');
        localStorage.removeItem('quizCompleted');
      } else {
        console.log('App.js: Квиз уже пройден, оставляем флаг');
      }
    }
  }, [user]);

  // Авторизация и загрузка пользователя и данных
  React.useEffect(() => {
    const doAuthAndLoad = async () => {
      const result = await auth();
      if (result.success) {
        await fetchUser();
        setUserLoaded(true); // Пользователь загружен
        await fetchPackages();
        await fetchCases();
        await fetchReferrals();
        await fetchItems();
        await fetchChallenges();
      } else {
        setUserLoaded(true); // Даже если авторизация не удалась, считаем загрузку завершенной
      }
    };
    doAuthAndLoad();
  }, []); // Убираем функции из зависимостей, чтобы избежать зацикливания

  // Функция для обработки start_param
  const handleStartParam = (startParam) => {
    if (!startParam) return;
    console.log('startParam:', startParam);
    const match = startParam.match(/^item_([a-f0-9-]+)_ref([a-f0-9]+)$/);
    if (match) {
      const [, itemId] = match;
      console.log('Parsed itemId:', itemId);
      if (!agreed) {
        setPendingStartParam({ itemId });
        return;
      }
      navigate(`/skin/${itemId}`);
    } else {
      console.log('startParam format not recognized:', startParam);
    }
  };

  // Убираем проверку соглашений - но не устанавливаем agreed сразу
  React.useEffect(() => {
    setAgreementsChecked(true);
  }, []);

  // Обработка start_param при загрузке
  React.useEffect(() => {
    if (!agreementsChecked) return;
    const startParam = WebApp.initDataUnsafe.start_param;
    handleStartParam(startParam);
  }, [agreementsChecked]);

  // Обработка редиректа после согласия с условиями
  React.useEffect(() => {
    if (agreed && pendingStartParam) {
      console.log('User agreed, redirecting to:', pendingStartParam.itemId);
      navigate(`/skin/${pendingStartParam.itemId}`);
      setPendingStartParam(null);
    }
  }, [agreed, pendingStartParam, navigate]);

  // Обработка редиректа на онбординг, если не согласился
  React.useEffect(() => {
    if (agreementsChecked && !agreed) {
      navigate('/onboarding', { replace: true });
    }
  }, [agreementsChecked, agreed, navigate]);

  React.useEffect(() => {
    console.log('WebApp.platform', WebApp.platform);
    if (WebApp.platform === 'android' || WebApp.platform === 'ios') {
      // Запрашиваем полноэкранный режим
      WebApp.requestFullscreen();
      // Расширяем область просмотра
      WebApp.expand();
    }
  }, []);


  React.useEffect(() => {
    ReactPixel.init('516059064928234');
    ReactPixel.pageView();
  }, []);

  React.useEffect(() => {
    const startParam = WebApp.initDataUnsafe.start_param;
    if (startParam?.startsWith('ads_')) {
      ReactPixel.trackCustom('ADS_START_FRONT');
    }
    if (startParam?.startsWith('ref_')) {
      ReactPixel.trackCustom('REFERAL_START_FRONT');
    }
  }, []);

  React.useEffect(() => {
    // Разблокируем воспроизведение аудио на первый пользовательский жест
    setupAudioUnlock();
    const startParam = WebApp.initDataUnsafe?.start_param || WebApp.initDataUnsafe?.startapp;
    const alreadyRedirected = sessionStorage.getItem('referralsChallengeRedirected');
    if (
      (startParam === 'referrals-challenge' || startParam === '/referrals-challenge') &&
      location.pathname !== '/referrals-challenge' &&
      !alreadyRedirected
    ) {
      sessionStorage.setItem('referralsChallengeRedirected', '1');
      navigate('/referrals-challenge', { replace: true });
    }
    // eslint-disable-next-line
  }, [navigate, location.pathname]);

  if (location.pathname === '/banned') {
    return <BannedPage />;
  }

  if (!agreementsChecked || !userLoaded) {
    return <div style={{ color: '#fff', textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div>;
  }

  // Проверяем, проходил ли пользователь квиз и является ли он новым пользователем
  const timeDiff = user && user.createdAt ? (Date.now() - new Date(user.createdAt).getTime()) : null;
  const isNewUser = user && user.createdAt &&
    (timeDiff > 0 ? timeDiff < 2 * 60 * 1000 : true); // Если дата в будущем, считаем новым пользователем

  // Определяем, нужно ли показывать квиз
  const shouldShowQuiz = isNewUser && !localStorage.getItem('quizCompleted');

  console.log('App.js: localStorage quizCompleted =', localStorage.getItem('quizCompleted'));
  console.log('App.js: user.createdAt =', user?.createdAt);
  console.log('App.js: timeDiff =', timeDiff);
  console.log('App.js: isNewUser =', isNewUser);
  console.log('App.js: shouldShowQuiz =', shouldShowQuiz);
  console.log('App.js: location.pathname =', location.pathname);
  console.log('App.js: userLoaded =', userLoaded);

  if (shouldShowQuiz && location.pathname === '/') {
    console.log('App.js: перенаправляем на /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  if (user && user.isBanned) {
    return <Navigate to="/banned" replace />;
  }

  return (
    <StyledThemeProvider theme={currentTheme}>
      <GlobalStyles />
      <ScrollToTop />
      <Routes>
        <Route path="/banned" element={<BannedPage />} />
        <Route path="/case-contents/:caseId" element={<CaseContentsView />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="cases" element={<Cases />} />
          <Route path="skins" element={<MySkins />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="referrals-challenge" element={<Referrals />} />
          <Route path="profile" element={<ProfileSection />} />
          <Route path="case-result/:assetId" element={<CaseResult />} />
          <Route path="skin/:weaponId" element={<SkinDetails />} />
          <Route path="packages" element={<Packages />} />
          <Route path="trade-url" element={<TradeUrlPage />} />
          <Route path="farm" element={<TokenFarming />} />
          <Route path="withdraw" element={<WithdrawPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="support/ticket/:ticketId" element={<TicketChat />} />
          <Route path="music" element={<MusicPage />} />
          <Route path="language" element={<LanguagePage />} />
          <Route path="claim-frags" element={<ClaimFragsPage />} />
        </Route>
        <Route path="/login" element={<LoginView />} />
        <Route path="/onboarding" element={<OnboardingView setAgreed={setAgreed} />} />
        <Route path="/case/:caseId" element={<CaseView />} />
        <Route path="/case-result/:weaponId" element={<CaseResult />} />
        <Route path="/inventory" element={<MySkins />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer position="bottom-center" autoClose={3000} />
    </StyledThemeProvider>
  );
};

function MaintenancePage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
      <h1 style={{ fontSize: 32, marginBottom: 16 }}>Технические работы</h1>
      <p style={{ fontSize: 18, color: '#888' }}>Сервис временно недоступен.<br />Пожалуйста, попробуйте позже.</p>
    </div>
  );
}

function WebOnlyBlock() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
      <h1 style={{ fontSize: 28, marginBottom: 16, color: '#222' }}>Пожалуйста, откройте приложение в Telegram</h1>
      <p style={{ fontSize: 18, color: '#888', textAlign: 'center' }}>
        Данное приложение работает только внутри Telegram (мобильное приложение или десктоп-клиент).<br />
        Запустите его через Telegram, чтобы продолжить.
      </p>
    </div>
  );
}

function App() {
  const [agreed, setAgreed] = React.useState(false);
  const isMaintenance = useMaintenanceCheck();
  console.log('process.env.REACT_APP_ENV', process.env.REACT_APP_ENV);
  // Проверяем платформу до любых хуков с побочными эффектами
  if (process.env.REACT_APP_ENV !== 'local') {
    if (WebApp.platform === 'web' || WebApp.platform === 'unknown') {
      return <WebOnlyBlock />;
    }
  }

  if (isMaintenance) return <MaintenancePage />;
  return (
    <ThemeProvider>
      <CollectionProvider>
        <SoundProvider>
          <QuizProvider>
            <AgreementContext.Provider value={{ agreed }}>
              <AppContent setAgreed={setAgreed} />
            </AgreementContext.Provider>
          </QuizProvider>
        </SoundProvider>
      </CollectionProvider>
    </ThemeProvider>
  );
}

export default App;